import { RefObject, useLayoutEffect, useState } from 'react'

export interface DropdownPosition {
	top: number
	left: number
	width: number
}

/**
 * Positions a portaled dropdown directly under an anchor element.
 *
 * Returns viewport coordinates (for `position: fixed`) measured from the
 * anchor's bounding rect, re-measured on window resize and on scroll. The
 * scroll listener uses the capture phase so it also tracks scrolling inside
 * modal bodies and other nested scroll containers, not just the window.
 *
 * Returns `null` while closed (or before the first measurement), so callers
 * can gate rendering on a truthy value.
 */
export const usePortalDropdownPosition = (
	anchorRef: RefObject<HTMLElement | null>,
	isOpen: boolean,
): DropdownPosition | null => {
	const [position, setPosition] = useState<DropdownPosition | null>(null)

	useLayoutEffect(() => {
		if (!isOpen || !anchorRef.current) {
			setPosition(null)
			return
		}
		const updatePosition = () => {
			if (!anchorRef.current) return
			const rect = anchorRef.current.getBoundingClientRect()
			setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width })
		}
		updatePosition()
		window.addEventListener('resize', updatePosition)
		window.addEventListener('scroll', updatePosition, true)
		return () => {
			window.removeEventListener('resize', updatePosition)
			window.removeEventListener('scroll', updatePosition, true)
		}
	}, [isOpen, anchorRef])

	return position
}
